import { Box, DialogContent, MenuItem, Typography, Alert, Grid } from '@mui/material';
import { CustomSelect } from '../../../../../common/components';

/**
 * Tab de planillas del proyecto.
 * En edición permite reemplazarlas; el reset se confirma al guardar.
 */
const TemplatesTab = ({
  localProject,
  handleSelectChange,
  progressTemplates = [],
  qualityTemplates = [],
  editMode = false,
}) => {
  const assignedTemplateIds = (localProject?.project_task_assignments || [])
    .filter((assignment) => assignment.is_active !== false)
    .map((assignment) => assignment.uuid_task_template);

  const currentProgressTemplate = progressTemplates.find((template) =>
    assignedTemplateIds.includes(template.uuid)
  );
  const currentQualityTemplate = qualityTemplates.find((template) =>
    assignedTemplateIds.includes(template.uuid)
  );

  const progressTemplateValue =
    localProject?.progress_template ?? currentProgressTemplate?.uuid ?? '';
  const qualityTemplateValue =
    localProject?.quality_template ?? currentQualityTemplate?.uuid ?? '';
  const selectedProgressTemplate = progressTemplates.find(
    (template) => template.uuid === progressTemplateValue
  );
  const selectedQualityTemplate = qualityTemplates.find(
    (template) => template.uuid === qualityTemplateValue
  );

  const hasProgressTemplate = Boolean(progressTemplateValue);
  const hasQualityTemplate = Boolean(qualityTemplateValue);
  const hasTemplateChanges =
    editMode &&
    (progressTemplateValue !== (currentProgressTemplate?.uuid || '') ||
      qualityTemplateValue !== (currentQualityTemplate?.uuid || ''));

  return (
    <Box sx={{ width: '100%' }}>
      <DialogContent sx={{ display: 'flex' }}>
        <Box sx={{ width: '100%' }}>
          <Typography variant="h6" sx={{ mb: 3, color: 'primary.main', fontWeight: 'bold' }}>
            {editMode
              ? 'Plantillas Asignadas'
              : 'Selección de Plantillas (Obligatorio)'}
          </Typography>

          {editMode ? (
            <Alert severity="warning" sx={{ mb: 3 }}>
              Si cambiás las plantillas, al guardar se reinicia el proyecto a 0 y se eliminan los
              datos generados por la planilla anterior.
            </Alert>
          ) : (
            <Alert severity="info" sx={{ mb: 3 }}>
              Las plantillas son obligatorias para crear un proyecto. Al asignar las plantillas, se
              generarán automáticamente todas las tareas de seguimiento de progreso y control de
              calidad.
            </Alert>
          )}

          <Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <CustomSelect
                  label="Planilla de Progreso *"
                  name="progress_template"
                  value={progressTemplateValue}
                  onChange={handleSelectChange}
                  size="medium"
                  required
                  fullWidth
                >
                  <MenuItem value="">Seleccionar planilla de progreso</MenuItem>
                  {progressTemplates.map((template) => (
                    <MenuItem key={template.uuid} value={template.uuid}>
                      {template.name}
                    </MenuItem>
                  ))}
                </CustomSelect>
              </Grid>

              <Grid item xs={12} md={6}>
                <CustomSelect
                  label="Planilla de Calidad *"
                  name="quality_template"
                  value={qualityTemplateValue}
                  onChange={handleSelectChange}
                  size="medium"
                  required
                  fullWidth
                >
                  <MenuItem value="">Seleccionar planilla de calidad</MenuItem>
                  {qualityTemplates.map((template) => (
                    <MenuItem key={template.uuid} value={template.uuid}>
                      {template.name}
                    </MenuItem>
                  ))}
                </CustomSelect>
              </Grid>

              <Grid item xs={12}>
                {(hasProgressTemplate || hasQualityTemplate) && (
                  <Box
                    sx={{
                      mt: 1,
                      p: 2,
                      bgcolor: 'success.50',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'success.main',
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 'bold', color: 'success.dark', mb: 1 }}
                    >
                      Plantillas Seleccionadas:
                    </Typography>
                    {hasProgressTemplate && (
                      <Typography variant="body2" color="success.dark">
                        ✓ Planilla de Progreso: {selectedProgressTemplate?.name}
                      </Typography>
                    )}
                    {hasQualityTemplate && (
                      <Typography variant="body2" color="success.dark">
                        ✓ Planilla de Calidad: {selectedQualityTemplate?.name}
                      </Typography>
                    )}
                  </Box>
                )}

                {hasTemplateChanges && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    Hay cambios pendientes en las planillas. Al guardar se regenerarán las tareas
                    del proyecto desde cero.
                  </Alert>
                )}

                {(!hasProgressTemplate || !hasQualityTemplate) && (
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      bgcolor: 'error.50',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'error.main',
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 'bold', color: 'error.dark', mb: 1 }}
                    >
                      Plantillas Faltantes:
                    </Typography>
                    {!hasProgressTemplate && (
                      <Typography variant="body2" color="error.dark">
                        ✗ Debe seleccionar una planilla de progreso
                      </Typography>
                    )}
                    {!hasQualityTemplate && (
                      <Typography variant="body2" color="error.dark">
                        ✗ Debe seleccionar una planilla de calidad
                      </Typography>
                    )}
                  </Box>
                )}
              </Grid>
            </Grid>
          </Box>
        </Box>
      </DialogContent>
    </Box>
  );
};

export default TemplatesTab;
